/*
 * Footer.tsx renders the persistent footer.
 * Brand and links above compact system metadata and the complete disclaimer.
 */
import React, { useEffect, useState } from 'react';
import { useVersionInfo } from '../hooks/useVersionInfo';
import { getAppEnvLabel, isDevelopmentEnv } from '../utils/appEnv';
import { useUserStore } from '../store/userStore';
import { useIssueFormStore } from '../store/issueFormStore';
import { useI18n } from '../i18n';
import { getBackendVersionApi } from '../lib/versionApi';
import FooterLinks from './FooterLinks';

const currentYear = new Date().getFullYear();
const formattedBuildDate = new Date(__BUILD_TIMESTAMP__).toLocaleString('nl-NL', {
  dateStyle: 'short',
  timeStyle: 'short'
});

const Footer: React.FC = () => {
  const [aiVersion, setAiVersion] = useState<string>('');
  const versionInfo = useVersionInfo();
  const mode = getAppEnvLabel();
  const currentUser = useUserStore(state => state.currentUser);
  const openIssueForm = useIssueFormStore(state => state.open);
  const { t } = useI18n();

  useEffect(() => {
    const fetchAiVersion = async () => {
      try {
        const data = await getBackendVersionApi();
        if (!data) return;
        setAiVersion(typeof data.version === 'string' ? data.version : '');
      } catch {
        setAiVersion('');
      }
    };

    fetchAiVersion();
  }, []);

  const getBuildDisplay = () => (versionInfo ? `#${versionInfo.buildNumber}` : '');

  const modeClassName = isDevelopmentEnv()
    ? 'bg-blue-100 text-blue-700'
    : 'bg-gray-100 text-gray-700';

  return (
    <footer className="hidden bg-transparent font-sans min-[900px]:block">
      <div className="mx-auto max-w-7xl px-6 py-6">
        <div className="flex flex-col gap-4 min-[1100px]:flex-row min-[1100px]:items-center min-[1100px]:justify-between">
          <div className="flex min-w-0 items-center gap-3.5">
            <img src="/we-dosports-icon-orange-trans.png" alt="" className="h-11 w-11 shrink-0 object-contain" />
            <div>
              <p className="text-base font-bold tracking-tight text-gray-800">doHockey</p>
              <p className="mt-1 text-xs text-gray-500">
                © 2025 – {currentYear} · {t('footer_built_with_ai')}
                <a className="text-primary-600 hover:text-primary-700" href="https://waedo.nl/">Waedo</a> in Oirschot
              </p>
            </div>
          </div>
          <FooterLinks onFeedback={currentUser ? () => openIssueForm() : undefined} />
        </div>
        <div className="mt-3 flex flex-wrap items-center justify-between gap-x-6 gap-y-2 text-[11px] text-gray-500">
          <div className="flex flex-wrap items-center gap-x-4 gap-y-1">
            <span>Platform v{versionInfo?.version}{getBuildDisplay()}</span>
            <span>AI Engines v{aiVersion || '-'}</span>
            <span className={`rounded px-2 py-0.5 text-[10px] font-medium ${modeClassName}`}>{mode}</span>
          </div>
          <span>{t('footer_build_timestamp', { date: formattedBuildDate })}</span>
        </div>
        <div className="mt-3 grid grid-cols-[5.5rem_minmax(0,1fr)] items-baseline gap-4 border-t border-gray-200 pt-3 text-[11px] text-gray-500">
          <span className="text-[10px] font-semibold uppercase tracking-wider">{t('footer_disclaimer_title')}</span>
          <p className="leading-relaxed">{t('footer_disclaimer_body')}</p>
        </div>
      </div>
    </footer>
  );
};

export default Footer;
